import { useState, useRef, useEffect, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { useChatStore, nextTempId } from '../../stores/chatStore'
import type { Message, MessageStatus } from '../../stores/chatStore'

interface PhotoUploadProps {
  onClose: () => void
  onSuccess?: (result: PhotoUploadResult) => void
}

export interface PhotoUploadResult {
  message_id: number
  emotion_detected: string | null
  emotion_confidence: number | null
  analysis_summary: string | null
  therapist_response: string | null
  therapist_name: string | null
  assistant_message_id: number | null
  voice_url: string | null
  video_urls: string[] | null
  status: string | null  // "processing" (async) or "complete" (sync fallback)
  image_url: string | null
}

type Mode = 'select' | 'camera' | 'preview'

async function uploadPhoto(file: File): Promise<PhotoUploadResult> {
  const formData = new FormData()
  formData.append('file', file)

  const { useAuthStore } = await import('../../stores/authStore')
  const token = useAuthStore.getState().accessToken

  const response = await fetch('/api/v1/media/image/upload', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Upload failed: ${response.status} ${error}`)
  }

  return response.json()
}

export default function PhotoUpload({ onClose, onSuccess }: PhotoUploadProps) {
  const [mode, setMode] = useState<Mode>('select')
  const [preview, setPreview] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [cameraLoading, setCameraLoading] = useState(false)

  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const tempIdRef = useRef<number | null>(null)
  const uploadedRef = useRef(false)

  const queryClient = useQueryClient()
  const addMessage = useChatStore((state) => state.addMessage)

  const mutation = useMutation({
    mutationFn: uploadPhoto,
    onSuccess: (result) => {
      // Update optimistic message with real ID
      // If async (status="processing"), mark as 'sent' — will become 'seen' via WS
      // If sync (status="complete"), mark as 'seen' immediately
      const tempId = tempIdRef.current
      if (tempId) {
        const isAsync = result.status === 'processing'
        const newStatus: MessageStatus = isAsync ? 'sent' : 'seen'
        const messages = useChatStore.getState().messages
        const updatedMessages = messages.map((m) =>
          m.id === tempId
            ? {
                ...m,
                id: result.message_id,
                imageUrl: result.image_url || m.imageUrl,
                status: newStatus,
              }
            : m
        )
        useChatStore.getState().setMessages(updatedMessages)
        tempIdRef.current = null
      }

      // Add therapist response only if it came in HTTP (sync mode)
      // In async mode, therapist response arrives via WebSocket "message" event
      if (result.therapist_response && result.status !== 'processing') {
        const assistantId = result.assistant_message_id || nextTempId()
        addMessage({
          id: assistantId,
          content: result.therapist_response,
          role: 'assistant',
          mediaType: 'text',
          voiceUrl: result.voice_url || undefined,
          videoUrls: result.video_urls || undefined,
          createdAt: new Date().toISOString(),
        })
      }
      queryClient.invalidateQueries({ queryKey: ['chatHistory'] })
      onSuccess?.(result)
    },
    onError: () => {
      // Update optimistic message to show error state
      const tempId = tempIdRef.current
      if (tempId) {
        useChatStore.getState().updateMessage(tempId, { status: 'error' })
      }
      tempIdRef.current = null
    },
  })

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
  }, [])

  // Cleanup on unmount — don't revoke preview if it was sent to optimistic message
  useEffect(() => {
    return () => {
      stopCamera()
      if (preview && !uploadedRef.current) URL.revokeObjectURL(preview)
    }
  }, [stopCamera, preview])

  // Start camera
  const openCamera = async () => {
    setCameraError(null)
    setCameraLoading(true)
    setMode('camera')

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } },
        audio: false,
      })
      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
    } catch {
      setCameraError('Не удалось получить доступ к камере')
      setMode('select')
    } finally {
      setCameraLoading(false)
    }
  }

  // Connect stream to video element when it mounts
  useEffect(() => {
    if (mode === 'camera' && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current
    }
  }, [mode])

  // Capture frame from video
  const capturePhoto = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) {
      setCameraError('Камера не готова. Попробуйте ещё раз.')
      return
    }

    canvas.width = video.videoWidth || 640
    canvas.height = video.videoHeight || 640

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      setCameraError('Не удалось создать контекст для снимка')
      return
    }

    // Mirror horizontally (front camera is mirrored in preview)
    ctx.translate(canvas.width, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob(
      (blob) => {
        if (!blob) return
        const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' })
        setSelectedFile(file)
        // Revoke old blob URL before creating new one
        if (preview && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview)
        }
        setPreview(URL.createObjectURL(blob))
        stopCamera()
        setMode('preview')
      },
      'image/jpeg',
      0.9
    )
  }

  // Gallery file select
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setFileError(null)
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setFileError('Поддерживаются только JPG, PNG, WebP')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setFileError('Максимальный размер файла — 10 МБ')
      return
    }

    setSelectedFile(file)

    const reader = new FileReader()
    reader.onload = (ev) => {
      setPreview(ev.target?.result as string)
      setMode('preview')
    }
    reader.readAsDataURL(file)
  }

  const openGallery = () => {
    galleryInputRef.current?.click()
  }

  const handleUpload = () => {
    if (!selectedFile) return

    // Optimistic UI: add message immediately, close modal
    const tempId = nextTempId()
    tempIdRef.current = tempId

    const optimisticMessage: Message = {
      id: tempId,
      role: 'user',
      content: '',
      mediaType: 'image',
      status: 'sending',
      imageUrl: preview || undefined,
      createdAt: new Date().toISOString(),
    }
    useChatStore.getState().addMessage(optimisticMessage)

    uploadedRef.current = true
    mutation.mutate(selectedFile)
    stopCamera()
    onClose()
  }

  const clearSelection = () => {
    if (preview) URL.revokeObjectURL(preview)
    setPreview(null)
    setSelectedFile(null)
    setMode('select')
  }

  const handleClose = () => {
    stopCamera()
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-end z-50 p-4 pb-20">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-800">
              {mode === 'camera' ? 'Камера' : 'Отправить фото'}
            </h2>
            <button
              onClick={handleClose}
              className="p-1 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* === CAMERA MODE === */}
          {mode === 'camera' && (
            <>
              <div className="relative rounded-xl overflow-hidden mb-4 bg-neutral-900 aspect-square">
                {cameraLoading && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  </div>
                )}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => { stopCamera(); setMode('select') }}
                  className={clsx(
                    'flex-1 py-2.5 px-4 rounded-xl font-medium',
                    'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                    'transition-colors'
                  )}
                >
                  Назад
                </button>
                <button
                  onClick={capturePhoto}
                  className={clsx(
                    'flex-1 py-2.5 px-4 rounded-xl font-medium',
                    'bg-primary-500 text-white hover:bg-primary-600',
                    'transition-colors'
                  )}
                >
                  Снять
                </button>
              </div>
            </>
          )}

          {/* === PREVIEW MODE === */}
          {mode === 'preview' && preview && (
            <>
              <div className="relative rounded-xl overflow-hidden mb-4">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-64 object-cover"
                />
                <button
                  onClick={clearSelection}
                  className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={clearSelection}
                  className={clsx(
                    'flex-1 py-2.5 px-4 rounded-xl font-medium',
                    'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                    'transition-colors'
                  )}
                >
                  Другое фото
                </button>
                <button
                  onClick={handleUpload}
                  className={clsx(
                    'flex-1 py-2.5 px-4 rounded-xl font-medium',
                    'bg-primary-500 text-white hover:bg-primary-600',
                    'transition-colors'
                  )}
                >
                  Отправить
                </button>
              </div>
            </>
          )}

          {/* === SELECT MODE === */}
          {mode === 'select' && (
            <>
              <p className="text-sm text-neutral-500 text-center mb-6">
                Сделайте фото или выберите из галереи
              </p>

              <div className="flex gap-4">
                {/* Camera — opens webcam preview */}
                <button
                  onClick={openCamera}
                  className={clsx(
                    'flex-1 flex flex-col items-center gap-3 p-6 rounded-xl cursor-pointer',
                    'bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300',
                    'transition-colors'
                  )}
                >
                  <div className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-neutral-700">Камера</span>
                </button>

                {/* Gallery — opens file picker */}
                <button
                  onClick={openGallery}
                  className={clsx(
                    'flex-1 flex flex-col items-center gap-3 p-6 rounded-xl cursor-pointer',
                    'bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300',
                    'transition-colors'
                  )}
                >
                  <div className="w-12 h-12 rounded-full bg-neutral-500 text-white flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-neutral-700">Галерея</span>
                </button>

                {/* Hidden file input for gallery */}
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="sr-only"
                />
              </div>
            </>
          )}

          {/* Error messages */}
          {(mutation.isError || cameraError || fileError) && (
            <p className="mt-4 text-sm text-error text-center">
              {fileError || cameraError || 'Ошибка загрузки. Попробуйте ещё раз.'}
            </p>
          )}
        </div>
      </div>

      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  )
}
